import { FaChartLine, FaDollarSign, FaUsers, FaBriefcase, FaSignal, FaTrophy, FaChalkboardTeacher, FaWallet, FaCalendarAlt, FaLightbulb } from 'react-icons/fa';
import { images } from '../constants'; 

const careerIntro = {
    sub: "Join Our Team",
    heading: "Become a Part of Our Innovative Team",
    description: "At ConnectNet, we’re searching for skilled and passionate professionals to join our dynamic team. Explore exciting career opportunities in the telecom and broadband industry and help us connect communities. Start your journey with us today and be a part of revolutionizing the future of connectivity.",
    points: [
        {
            icon: <FaChartLine />,
            title: "Innovative Work Environment",
        },
        {
            icon: <FaDollarSign />,
            title: "Competitive Compensation",
        },
        {
            icon: <FaUsers />,
            title: "Collaborative Team Culture",
        },
        {
            icon: <FaBriefcase />,
            title: "Career Development",
        },
        {
            icon: <FaSignal />,
            title: "Growth Opportunities",
        },
    ],
    image: {
        src: images.careerIntro,
        alt: "Telecom and Broadband Career Image",
    },
};

const benefits = [
    {
        id: 1,
        icon: <FaTrophy />,
        title: "Career Growth",
        description: "Explore exciting paths for professional advancement within the broadband and telecommunications industry.",
    },
    {
        id: 2,
        icon: <FaChalkboardTeacher />,
        title: "Skill Development",
        description: "Enhance your technical expertise with extensive training programs and workshops.",
    },
    {
        id: 3,
        icon: <FaWallet />,
        title: "Competitive Salary",
        description: "Enjoy a rewarding pay structure with competitive industry rates and performance bonuses.",
    },
    {
        id: 4,
        icon: <FaCalendarAlt />,
        title: "Flexible Hours",
        description: "Benefit from adaptable work schedules to balance personal and professional commitments.",
    },
    {
        id: 5,
        icon: <FaUsers />,
        title: "Supportive Team",
        description: "Collaborate with a dynamic team that values innovation and teamwork.",
    },
    {
        id: 6,
        icon: <FaLightbulb />,
        title: "Innovation Focus",
        description: "Work in an environment that encourages creative ideas to drive industry-leading services.",
    },
];

const positions = [
    {
        id: 1,
        image: images.position1,
        title: 'Support Agent',
        content: 'Provide customer assistance and troubleshoot connectivity issues.',
        details: {
            location: 'ConnectNet, City',
            timing: 'Full Time',
            hours: 'Standard Business Hours',
            experience: 'Preferred: 1-2 Years in Customer Support',
            position: 'Multiple Positions Available',
            days: 'Monday to Friday',
            salary: '$40K - $55K',
            deadline: 'Apply Now - Open Until Filled',
            number: 'For Assistance: Call 123-456-7890',
        },
        overviews: [
            'Provide customer assistance and troubleshoot connectivity issues efficiently. Support Agents will handle inquiries, resolve technical problems, and ensure a positive customer experience.',
        ],
        responsibilities: [
            'Responding promptly to customer calls and emails.',
            'Diagnosing and resolving internet and cable service issues.',
            'Documenting customer interactions and troubleshooting steps.',
            'Escalating complex problems to technical teams when necessary.',
            'Providing clear instructions and guidance to customers.',
            'Maintaining up-to-date knowledge of products and services.',
        ],
        qualifications: [
            'High school diploma or equivalent.',
            'Experience in customer service or technical support preferred.',
            'Strong communication and interpersonal skills.',
            'Ability to work well in a team environment.',
            'Problem-solving skills and patience with customers.',
            'Basic technical knowledge of internet and cable services.',
        ],
    },
    {
        id: 2,
        image: images.position2,
        title: 'Network Specialist',
        content: 'Optimize and maintain network for high-speed broadband services.',
        details: {
            location: 'ConnectNet, City',
            timing: 'Full Time',
            hours: 'Standard Business Hours',
            experience: 'Preferred: 3+ Years in Network Management',
            position: 'One Position Available',
            days: 'Monday to Friday',
            salary: '$70K - $90K',
            deadline: 'Apply Now - Open Until Filled',
            number: 'For Assistance: Call 123-456-7890',
        },
        overviews: [
            'Optimize and maintain the company’s network infrastructure to ensure reliable, high-speed broadband services. The Network Specialist will troubleshoot network issues, implement upgrades, and maintain security standards.',
        ],
        responsibilities: [
            'Monitor and manage network performance and reliability.',
            'Configure and install network hardware and software.',
            'Troubleshoot network outages and connectivity issues.',
            'Implement security protocols to protect network data.',
            'Collaborate with IT teams to upgrade infrastructure.',
            'Document network configurations and changes.',
        ],
        qualifications: [
            'Bachelor’s degree in Computer Science, IT, or related field preferred.',
            'Minimum 3 years of experience in network administration or engineering.',
            'Strong knowledge of network protocols, hardware, and security.',
            'Experience with routers, switches, firewalls, and VPNs.',
            'Problem-solving skills and ability to work under pressure.',
            'Excellent communication and teamwork abilities.',
        ],
    },
    {
        id: 3,
        image: images.position3,
        title: 'Sales Manager',
        content: 'Lead sales team to promote broadband packages and services.',
        details: {
            location: 'ConnectNet, City',
            timing: 'Full Time',
            hours: 'Standard Business Hours',
            experience: 'Preferred: 3+ Years in Sales Management',
            position: 'One Position Available',
            days: 'Monday to Friday',
            salary: '$65K - $85K',
            deadline: 'Apply Now - Open Until Filled',
            number: 'For Assistance: Call 123-456-7890',
        },
        overviews: [
            'Lead and manage the sales team to effectively promote broadband packages and services. Drive sales growth through strategic planning, coaching, and customer engagement.',
        ],
        responsibilities: [
            'Develop and implement sales strategies to meet targets.',
            'Manage and motivate the sales team for optimal performance.',
            'Analyze market trends and competitor activities.',
            'Build and maintain strong customer relationships.',
            'Prepare sales reports and forecasts for management.',
            'Coordinate with marketing and technical teams.',
        ],
        qualifications: [
            'Bachelor’s degree in Business, Marketing, or related field preferred.',
            'Minimum 3 years of experience in sales management.',
            'Strong leadership and team management skills.',
            'Excellent communication and negotiation abilities.',
            'Ability to analyze sales data and market trends.',
            'Customer-focused with problem-solving capabilities.',
        ],
    },
    {
        id: 4,
        image: images.position4,
        title: 'Service Technician',
        content: 'Install and troubleshoot equipment for internet and cable services.',
        details: {
            location: 'ConnectNet, City',
            timing: 'Full Time',
            hours: 'Standard Business Hours / Flexible Shifts',
            experience: 'Preferred: 2+ Years in Service or Technical Support',
            position: 'Multiple Positions Available',
            days: 'Monday to Saturday, Rotational Shifts',
            salary: '$50K - $65K',
            deadline: 'Apply Now - Open Until Filled',
            number: 'For Assistance: Call 123-456-7890',
        },
        overviews: [
            'Install, maintain, and troubleshoot equipment for internet and cable services to ensure optimal performance and customer satisfaction.',
        ],
        responsibilities: [
            'Install internet and cable service equipment at customer premises.',
            'Diagnose and resolve technical issues promptly.',
            'Perform routine maintenance and upgrades on equipment.',
            'Ensure compliance with safety and service standards.',
            'Provide technical support and guidance to customers.',
            'Collaborate with the technical and customer care teams.',
        ],
        qualifications: [
            'High school diploma or equivalent.',
            'Minimum 2 years of experience in service technician role.',
            'Knowledge of internet, cable systems, and networking equipment.',
            'Strong troubleshooting and problem-solving skills.',
            'Good communication and customer service skills.',
            'Ability to work independently and in a team environment.',
        ],
    },
    {
        id: 5,
        image: images.position5,
        title: 'Billing Coordinator',
        content: 'Manage billing accounts and assist customers with payment inquiries.',
        details: {
            location: 'ConnectNet, City',
            timing: 'Full Time',
            hours: 'Standard Business Hours',
            experience: 'Preferred: 1-3 Years in Billing or Customer Service',
            position: 'Multiple Positions Available',
            days: 'Monday to Friday',
            salary: '$45K - $60K',
            deadline: 'Apply Now - Open Until Filled',
            number: 'For Assistance: Call 123-456-7890',
        },
        overviews: [
            'Manage billing accounts accurately and provide timely assistance to customers regarding their payment inquiries and billing concerns.',
        ],
        responsibilities: [
            'Process and maintain customer billing information and records.',
            'Respond to customer inquiries related to billing and payments.',
            'Coordinate with finance and customer service departments to resolve billing issues.',
            'Ensure timely and accurate invoicing and payments.',
            'Maintain confidentiality and data security.',
            'Prepare reports related to billing activities as needed.',
        ],
        qualifications: [
            'High school diploma or equivalent; associate or bachelor’s degree preferred.',
            '1-3 years of experience in billing, accounting, or customer service.',
            'Strong attention to detail and organizational skills.',
            'Good communication and problem-solving abilities.',
            'Familiarity with billing software and MS Office applications.',
            'Ability to handle sensitive information with discretion.',
        ],
    },
    {
        id: 6,
        image: images.position6,
        title: 'Product Analyst',
        content: 'Analyze trends to improve broadband services and offerings.',
        details: {
            location: 'ConnectNet, City',
            timing: 'Full Time',
            hours: 'Standard Business Hours',
            experience: 'Preferred: 2-4 Years in Product Analysis or Data Analytics',
            position: 'Multiple Positions Available',
            days: 'Monday to Friday',
            salary: '$55K - $75K',
            deadline: 'Apply Now - Open Until Filled',
            number: 'For Assistance: Call 123-456-7890',
        },
        overviews: [
            'Analyze market and service trends to provide actionable insights that help improve broadband service offerings and customer satisfaction.',
        ],
        responsibilities: [
            'Collect and analyze data related to broadband services and customer usage patterns.',
            'Identify trends, opportunities, and areas for improvement.',
            'Prepare detailed reports and presentations for stakeholders.',
            'Collaborate with product development and marketing teams to refine services.',
            'Monitor competitor products and industry developments.',
            'Support strategic planning with data-driven insights.',
        ],
        qualifications: [
            'Bachelor’s degree in Business, Statistics, Data Science, or related field.',
            '2-4 years of experience in product analysis, data analytics, or related roles.',
            'Strong analytical and problem-solving skills.',
            'Proficiency with data analysis tools such as Excel, SQL, or BI software.',
            'Excellent communication and presentation skills.',
            'Ability to work collaboratively across departments.',
        ],
    }

];

export { positions, benefits, careerIntro };